import React, { useEffect, useState } from "react";
import { PasswordInput } from "@/components/ui/password-input";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Mail, Lock, User as UserIcon, School as SchoolIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import WelcomeInviteDialog from "@/components/WelcomeInviteDialog";

export default function InvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", password2: "" });
  const [saving, setSaving] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/invites/${token}`);
        setInvite(r.data);
        // Always show the welcome dialog on first visit (per browser session).
        const seenKey = `amc_welcome_seen_${token}`;
        if (!sessionStorage.getItem(seenKey)) {
          setWelcomeOpen(true);
          sessionStorage.setItem(seenKey, "1");
        }
      } catch (e) {
        setInvite(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe seu nome");
    if (!form.email.trim()) return toast.error("Informe seu e-mail");
    if (form.password.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres");
    if (form.password !== form.password2) return toast.error("As senhas não conferem");

    setSaving(true);
    try {
      const r = await api.post("/auth/school-register", {
        invite_token: token,
        email: form.email,
        password: form.password,
        name: form.name,
      });
      setUser(r.data);
      toast.success("Cadastro realizado! Bem-vindo(a).");
      nav("/escola", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#7c3aed]"></div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center card-shadow">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="font-heading text-xl font-bold text-slate-900">Link inválido</h1>
          <p className="text-sm text-slate-600 mt-2">
            Este link de convite não é mais válido ou expirou. Entre em contato com a AMC Escolas
            para receber um novo acesso.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <WelcomeInviteDialog
        open={welcomeOpen}
        onOpenChange={setWelcomeOpen}
        schoolName={invite.school_name}
      />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl p-8 card-shadow">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#1e3a8a] flex items-center justify-center mb-3">
              <SchoolIcon className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
            <p className="text-xs uppercase tracking-widest text-[#7c3aed] font-bold mb-1">
              Convite AMC Escolas
            </p>
            <h1 className="font-heading text-2xl font-black text-[#1e3a8a]">{invite.school_name}</h1>
            {invite.school_city && (
              <p className="text-sm text-slate-500">{invite.school_city}</p>
            )}
            <p className="text-sm text-slate-600 mt-3 max-w-xs">
              Crie seu acesso para visualizar os orçamentos e solicitar novos serviços.
            </p>
            <button
              type="button"
              onClick={() => setWelcomeOpen(true)}
              className="mt-2 text-xs text-[#7c3aed] font-semibold hover:underline"
              data-testid="welcome-invite-reopen"
            >
              Ver o que você encontra no sistema
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nome completo</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="invite-input-name"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="Seu nome"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-input-email"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="voce@escola.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute z-10 pointer-events-none left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <PasswordInput
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="invite-input-password"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirmar senha</label>
              <div className="relative">
                <Lock className="absolute z-10 pointer-events-none left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <PasswordInput
                  type="password"
                  value={form.password2}
                  onChange={(e) => setForm({ ...form, password2: e.target.value })}
                  data-testid="invite-input-password2"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="Repita a senha"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full h-11 bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl font-semibold"
              data-testid="invite-submit"
            >
              {saving ? "Cadastrando..." : "Criar meu acesso"}
            </Button>
            {invite.has_users && (
              <p className="text-xs text-center text-slate-500">
                Já possui cadastro?{" "}
                <a href="/escola/login" className="text-[#7c3aed] font-semibold hover:underline">
                  Entrar
                </a>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
